import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ShieldAlert, ShieldCheck, AlertTriangle, ChevronRight, CalendarPlus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_BY_KIND = {
  mot: ShieldCheck,
  insurance: ShieldCheck,
  tax: ShieldCheck,
  service: ShieldCheck,
  tyre: ShieldCheck,
};

const URGENCY_TONE = {
  overdue:  "bg-rose-600/15 border-rose-500/40 text-rose-200",
  due_soon: "bg-amber-500/15 border-amber-500/40 text-amber-200",
  upcoming: "bg-violet-600/15 border-violet-500/40 text-violet-200",
  later:    "bg-slate-800 border-slate-700 text-slate-300",
};

/**
 * ComplianceWidget — sits on instructor dashboard.
 * Surfaces upcoming compliance items (MOT/insurance/tax/service/tyre).
 * Tapping the card navigates to /instructor/compliance for full management.
 */
export default function ComplianceWidget() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/compliance/upcoming?within_days=60`, { withCredentials: true });
      setItems(data.items || []);
    } catch {
      /* silent */
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!loaded) return null;

  // No dates configured at all → invite to set up
  if (items.length === 0) {
    return (
      <button
        data-testid="compliance-empty"
        onClick={() => nav("/instructor/compliance")}
        className="w-full text-left rounded-2xl bg-slate-900 border border-dashed border-slate-700 p-4 hover:border-violet-500 transition"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-600/15 text-violet-300 flex items-center justify-center">
            <ShieldAlert className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-extrabold text-white">Set up your compliance reminders</p>
            <p className="text-[11px] text-slate-400 mt-0.5">MOT · insurance · tax · service · tyre cycle.</p>
          </div>
          <ChevronRight className="w-4 h-4 text-slate-500" />
        </div>
      </button>
    );
  }

  return (
    <div data-testid="compliance-widget" className="rounded-2xl bg-slate-900 border border-slate-800 p-4 text-white">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-violet-400">
            Upcoming compliance
          </p>
          <p className="text-[12px] text-slate-300">Stay legal, stay earning.</p>
        </div>
        <button
          data-testid="compliance-open"
          onClick={() => nav("/instructor/compliance")}
          className="text-[11px] font-extrabold text-violet-300 hover:text-violet-200 inline-flex items-center"
        >
          Manage <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      <ul data-testid="compliance-items" className="space-y-1.5">
        {items.slice(0, 4).map((it) => (
          <ItemRow key={`${it.kind}-${it.due_date}`} item={it} />
        ))}
      </ul>
    </div>
  );
}

function ItemRow({ item }) {
  const Icon = ICON_BY_KIND[item.kind] || ShieldCheck;
  const tone = URGENCY_TONE[item.urgency] || URGENCY_TONE.later;
  const urgencyLabel =
    item.urgency === "overdue" ? `${Math.abs(item.days_until)} days overdue` :
    item.days_until === 0 ? "Due today" :
    item.days_until === 1 ? "Due tomorrow" :
    `Due in ${item.days_until} days`;
  const Warn = item.urgency === "overdue" ? AlertTriangle : Icon;
  return (
    <li data-testid={`compliance-row-${item.kind}`} className={`rounded-xl border px-3 py-2 ${tone} flex items-center gap-2`}>
      <Warn className="w-4 h-4 shrink-0" />
      <p className="flex-1 min-w-0 text-[12px] font-extrabold truncate">{item.label}</p>
      <p className="text-[10px] font-extrabold uppercase tracking-wider">{urgencyLabel}</p>
    </li>
  );
}
